import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Immobilisation } from '@shared/types/Immobilisation';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import { fmtFCFA, esc, fmtDate } from '@presentation/shell/format';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { canMutateComptaRecords, canMutateRecords } from '@application/access/ViewAccessPolicy';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { ProvisionsOHADAService } from '@domain/comptabilite/ProvisionsOHADA';
import { EtatsFinanciersService } from '@domain/comptabilite/EtatsFinanciersService';
import { NotesAnnexesService } from '@domain/comptabilite/NotesAnnexes';
import { LiasseFiscaleService } from '@domain/fiscalite/LiasseFiscaleService';
import { IRVMService, type TypeRevenuCapitaux } from '@domain/fiscalite/IRVMService';
import { TaxesSectoriellesService } from '@domain/fiscalite/TaxesSectorielles';
import { ReserveLegaleService } from '@domain/gouvernance/ReserveLegale';
import { CalendrierService } from '@domain/rh/CalendrierService';
import { SoldeToutCompteService, type MotifSortieStc } from '@domain/rh/SoldeToutCompte';
import { SanctionsService } from '@domain/rh/SanctionsService';
import { listConges } from '@application/rh/CongesDirectoryService';
import { loadStcConfig } from '@application/rh/StcConfig';
import { loadAncienneteConfig } from '@application/rh/ConventionsCollectives';
import { CoffreFortStorageService } from '@application/persistence/CoffreFortStorageService';
import { loadRegister, saveRegister } from '@presentation/shell/localCrud';
import { downloadCsv, downloadBlob } from '@presentation/shell/download';
import { uuid } from '@shared/utils/uuid';
import {
  RMM,
  SEUIL_EXONERATION_TCS,
  SMIG,
  SEUIL_DELEGUE_PERSONNEL,
  SEUIL_CHS,
  SEUIL_REGLEMENT_INTERIEUR,
  PRECOMPTE_HONORAIRES_RES,
  SEUIL_CAC_OHADA_ACTIF,
  SEUIL_CAC_OHADA_CA,
  SEUIL_CAC_OHADA_EFFECTIF,
} from '@shared/constants/Taux';
import type { ImmobilisationLocal, StockArticle } from './erpSimple';
import {
  categorieDepuisAvantage,
  dureeAmortissement,
  comptesImmobilisation,
} from '@domain/comptabilite/ImmobilisationRegistre';
import type { RegimeIS } from '@domain/fiscalite/LiasseFiscaleService';

/** Profils UI ↔ régimes liasse (`Taux.ts` / MD2D). */
const PROFIL_TO_REGIME: Record<string, RegimeIS> = {
  normal: 'droit_commun',
  droit_commun: 'droit_commun',
  banques_mines: 'banques_mines',
  /** Pétrole ET mines : 35 % (CGI art. 16). */
  petrole_mines: 'petrole',
  petrole: 'petrole',
  mines: 'minier',
  /** Ancien libellé PME 25 % — aligné droit commun 30 % (pas de taux PME dans le référentiel). */
  pme: 'droit_commun',
};
const TAUX_LABEL: Record<string, string> = {
  normal: 'Droit commun — 30 %',
  droit_commun: 'Droit commun — 30 %',
  banques_mines: 'Banques — 30 % (droit commun)',
  petrole_mines: 'Pétrole / Mines — 35 %',
  petrole: 'Pétrole / Mines — 35 %',
  mines: 'Mines — 35 %',
  pme: 'Droit commun — 30 % (ex-PME)',
};

function tenantId(ctx: Parameters<ViewController>[1]): string {
  return ctx.tenantContext.tryGetTenant()?.id ?? 'default';
}

function yearOf(e: Ecriture): string {
  return (e.periode || e.date || '').slice(0, 4);
}

function soldeCompte(rows: readonly Ecriture[], prefix: string): number {
  return rows
    .filter((e) => e.compte.startsWith(prefix))
    .reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);
}

function kvTable(pairs: Array<[string, string]>): string {
  return pairs.map(([k, v]) => `<tr><td>${k}</td><td class="num">${v}</td></tr>`).join('');
}

function mapImmos(
  items: ImmobilisationLocal[],
  registre: Immobilisation[],
  tid: string,
): Immobilisation[] {
  const fromAn = items.map((i) => {
    const categorie = categorieDepuisAvantage(i.type);
    const comptes = comptesImmobilisation(categorie);
    return {
      id: i.id,
      tenant_id: tid,
      code: i.id,
      libelle: i.designation,
      categorie,
      date_acquisition: i.date_acquisition || new Date().toISOString().slice(0, 10),
      date_mise_service: i.date_acquisition || new Date().toISOString().slice(0, 10),
      valeur_acquisition: i.valeur || 0,
      duree_amortissement_annees: dureeAmortissement(categorie),
      mode: 'lineaire' as const,
      compte_immo: comptes.compte_immo,
      compte_amortissement: comptes.compte_amortissement,
      compte_dotation: comptes.compte_dotation,
      cree_le: i.date_acquisition || new Date().toISOString(),
    };
  });
  const seen = new Set(registre.map((r) => r.id));
  return [...registre, ...fromAn.filter((i) => !seen.has(i.id))];
}

export const conformiteController: ViewController = async (section, ctx, shell) => {
  const canMutateCompta = canMutateComptaRecords(shell.permission);
  const canMutateRh = canMutateRecords(shell.permission);
  const tid = tenantId(ctx);
  const tenant = ctx.tenantContext.tryGetTenant();
  const [
    ecrituresInit,
    salaries,
    bulletins,
    immosLocal,
    immosComptables,
    stocks,
    conges,
    stcConfig,
    ancienneteConfig,
  ] = await Promise.all([
    ctx.ecritures.list(),
    ctx.salaries.list(),
    ctx.bulletins.list(),
    loadRegister<ImmobilisationLocal>(tid, 'immobilisations'),
    loadRegister<Immobilisation>(tid, 'immobilisations_comptables'),
    loadRegister<StockArticle>(tid, 'stocks'),
    listConges(tid),
    loadStcConfig(tid),
    loadAncienneteConfig(tid),
  ]);
  let ecritures = ecrituresInit;

  const anneeSel = section.querySelector<HTMLSelectElement>('#conf-annee');
  if (anneeSel && anneeSel.options.length === 0) {
    const years = new Set(ecritures.map(yearOf).filter(Boolean));
    if (years.size === 0) {
      const cur = new Date().getFullYear();
      for (let y = cur; y >= cur - 2; y--) years.add(String(y));
    }
    for (const y of [...years].sort((a, b) => b.localeCompare(a))) {
      const o = document.createElement('option');
      o.value = y;
      o.textContent = y;
      anneeSel.appendChild(o);
    }
  }

  const savedProfil = await SecureTenantStore.getJson<string>(
    tid,
    'conformite.profil',
    'normal',
    `nexxus.conformite.${tid}.profil`,
  );
  const profilSelect = section.querySelector<HTMLSelectElement>('#conf-profil');
  if (profilSelect) profilSelect.value = savedProfil;
  const savedPatente = await SecureTenantStore.getJson<number>(
    tid,
    'conformite.patente',
    0,
    `nexxus.conformite.${tid}.patente`,
  );
  const patenteInput = section.querySelector<HTMLInputElement>('#conf-patente-forfait');
  if (patenteInput && !patenteInput.value) patenteInput.value = String(savedPatente);
  const savedSysteme = await SecureTenantStore.getJson<string>(
    tid,
    'conformite.systeme',
    'normal',
    `nexxus.conformite.${tid}.systeme`,
  );
  const systemeInput = section.querySelector<HTMLSelectElement>('#conf-systeme');
  if (systemeInput) systemeInput.value = savedSysteme;

  const stcSelect = section.querySelector<HTMLSelectElement>('#conf-stc-salarie');
  if (stcSelect && stcSelect.options.length === 0) {
    const empty = document.createElement('option');
    empty.value = '';
    empty.textContent = salaries.length === 0 ? 'Aucun salarié' : 'Choisir un salarié';
    stcSelect.appendChild(empty);
    for (const s of salaries) {
      const o = document.createElement('option');
      o.value = s.id;
      o.textContent = `${s.prenom} ${s.nom}`;
      stcSelect.appendChild(o);
    }
  }

  const anneeCourante = (): string => anneeSel?.value ?? String(new Date().getFullYear());
  const guardCompta = (): boolean => {
    if (canMutateCompta) return true;
    ToastService.warning('Action réservée aux profils autorisés');
    return false;
  };

  const renderStc = (): void => {
    const salId = section.querySelector<HTMLSelectElement>('#conf-stc-salarie')?.value ?? '';
    const motif = (section.querySelector<HTMLSelectElement>('#conf-stc-motif')?.value ??
      'demission') as MotifSortieStc;
    const conv = section.querySelector('#conf-stc-convention');
    if (conv) conv.textContent = stcConfig.convention || 'Loi 3/94 (légal)';
    const table = section.querySelector('#conf-stc-table tbody');
    const sal = salaries.find((s: Salarie) => s.id === salId);
    if (!table) return;
    if (!sal) {
      table.innerHTML =
        '<tr><td colspan="3" style="color:var(--gris);padding:12px;">Sélectionnez un salarié pour calculer le solde de tout compte.</td></tr>';
      return;
    }
    const result = SoldeToutCompteService.calculer({
      salarie: sal,
      motif,
      config: stcConfig,
      conges,
      ancienneteConfig,
      brut_total_cdd: bulletins
        .filter((b: Bulletin) => b.salarie_id === sal.id)
        .reduce((s, b) => s + (b.brut_imposable || 0), 0),
    });
    table.innerHTML =
      result.lignes
        .map(
          (l) =>
            `<tr><td>${esc(l.libelle)}</td><td class="num">${esc(l.base)}</td><td class="num">${esc(fmtFCFA(l.montant))}</td></tr>`,
        )
        .join('') +
      `<tr class="total"><td colspan="2"><strong>Total STC</strong></td><td class="num"><strong>${esc(fmtFCFA(result.total))}</strong></td></tr>`;
  };

  const render = (): void => {
    const annee = anneeCourante();
    const profil = section.querySelector<HTMLSelectElement>('#conf-profil')?.value ?? savedProfil;
    const patente = Number(
      section.querySelector<HTMLInputElement>('#conf-patente-forfait')?.value ?? savedPatente,
    );
    const rows = ecritures.filter((e) => yearOf(e) === annee);
    const affiche = section.querySelector('#conf-annee-affiche');
    if (affiche) affiche.textContent = annee;
    let produits = 0,
      charges = 0,
      actif = 0;
    for (const e of rows) {
      const cl = e.compte.charAt(0);
      if (cl === '7') produits += (e.credit || 0) - (e.debit || 0);
      else if (cl === '6') charges += (e.debit || 0) - (e.credit || 0);
      if (cl === '2' || cl === '3' || cl === '5') actif += (e.debit || 0) - (e.credit || 0);
    }
    const resultat = produits - charges;
    const regime = PROFIL_TO_REGIME[profil] ?? 'droit_commun';
    const md2dEstime = LiasseFiscaleService.genererMD2D(rows, annee, 0, 0, regime);
    const taux = md2dEstime.taux_is;
    const is = md2dEstime.is_du;
    const effectif = salaries.filter((s: Salarie) => s.statut === 'actif').length;
    const masseSalariale = bulletins
      .filter((b: Bulletin) => b.periode.startsWith(annee))
      .reduce((s, b) => s + (b.brut_imposable || 0), 0);
    const cr = EtatsFinanciersService.calculerCompteResultat(rows);
    const bilan = EtatsFinanciersService.calculerBilan(rows);
    const capital = tenant?.capital_social ?? 0;
    const reserveActuelle = Math.max(0, -soldeCompte(rows, '111'));
    const reserveCalc = ReserveLegaleService.calculer(
      annee,
      cr.resultat_net,
      capital,
      reserveActuelle,
    );
    const prov = ProvisionsOHADAService.genererEcritures(tid, {
      encours_clients: Math.max(0, soldeCompte(rows, '41')),
      masse_salariale: masseSalariale,
      annee,
    });

    const isTable = section.querySelector('#conf-is-table tbody');
    if (isTable) {
      isTable.innerHTML = kvTable([
        ['Régime fiscal', TAUX_LABEL[profil] ?? profil],
        ['Produits (classe 7)', fmtFCFA(produits)],
        ['Charges (classe 6)', fmtFCFA(charges)],
        [`Résultat comptable ${annee}`, fmtFCFA(resultat)],
        ['Taux IS applicable', `${(taux * 100).toFixed(0)} %`],
        ['IS calculé (résultat × taux)', fmtFCFA(md2dEstime.is_calcule)],
        ['IS minimum (max 1 % CA, 1 M)', fmtFCFA(md2dEstime.is_minimum)],
        ['IS estimé dû', fmtFCFA(is)],
      ]);
    }
    const patTable = section.querySelector('#conf-patente-table tbody');
    if (patTable) {
      patTable.innerHTML = kvTable([
        [`Forfait patente ${annee}`, fmtFCFA(patente)],
        ['Échéance', `28 février ${Number(annee) + 1}`],
      ]);
    }
    const seuils = [
      {
        label: `Total du bilan (actif) > ${fmtFCFA(SEUIL_CAC_OHADA_ACTIF)}`,
        valeur: actif,
        seuil: SEUIL_CAC_OHADA_ACTIF,
      },
      {
        label: `Chiffre d'affaires HT > ${fmtFCFA(SEUIL_CAC_OHADA_CA)}`,
        valeur: produits,
        seuil: SEUIL_CAC_OHADA_CA,
      },
      {
        label: `Effectif permanent > ${SEUIL_CAC_OHADA_EFFECTIF}`,
        valeur: effectif,
        seuil: SEUIL_CAC_OHADA_EFFECTIF,
      },
    ];
    const cacTable = section.querySelector('#conf-cac-table tbody');
    if (cacTable) {
      cacTable.innerHTML = seuils
        .map((s) => {
          const atteint = s.valeur > s.seuil;
          return `<tr><td>${s.label}</td><td class="num">${s.seuil >= 1000 ? fmtFCFA(s.valeur) : s.valeur}</td><td class="num"><span class="pill ${atteint ? 'warning' : 'success'}">${atteint ? 'Atteint' : 'Non atteint'}</span></td></tr>`;
        })
        .join('');
    }
    const verdict = section.querySelector<HTMLElement>('#conf-cac-verdict');
    if (verdict) {
      const nb = seuils.filter((s) => s.valeur > s.seuil).length;
      verdict.style.background = nb >= 2 ? '#FCF3CF' : '#D5F5E3';
      verdict.innerHTML =
        nb >= 2
          ? `<strong>Commissariat aux comptes obligatoire</strong> — ${nb}/3 seuils atteints.`
          : `<strong>Commissariat aux comptes non obligatoire</strong> — ${nb}/3 seuil(s) atteint(s).`;
    }
    const provTable = section.querySelector('#conf-prov-table tbody');
    if (provTable) {
      provTable.innerHTML = kvTable([
        ['Créances clients (411)', fmtFCFA(Math.max(0, soldeCompte(rows, '41')))],
        ['Masse salariale de l’exercice', fmtFCFA(masseSalariale)],
        ['Provision créances douteuses 5 % (491)', fmtFCFA(prov.total_creances_douteuses)],
        ['Provision congés payés 8,33 % (198)', fmtFCFA(prov.total_conges_payes)],
        [
          'Provision indemnités fin de carrière 2 % (1910)',
          fmtFCFA(prov.total_indemnites_fin_carriere),
        ],
        ['Total des dotations', fmtFCFA(prov.total_general)],
      ]);
    }
    const tafireTable = section.querySelector('#conf-tafire-table tbody');
    if (tafireTable) {
      tafireTable.innerHTML = kvTable([
        ['CAF (approx. résultat net)', fmtFCFA(cr.resultat_net)],
        ['Variation du BFR', fmtFCFA(bilan.actif_circulant - bilan.dettes_court_terme)],
        ['Investissements', fmtFCFA(bilan.actif_immobilise)],
        [
          'Variation de trésorerie nette',
          fmtFCFA(bilan.tresorerie_actif - bilan.tresorerie_passif),
        ],
      ]);
    }
    const notesHost = section.querySelector('#conf-notes-content');
    if (notesHost) {
      notesHost.innerHTML = NotesAnnexesService.genererToutes(
        rows,
        mapImmos(immosLocal, immosComptables, tid),
        annee,
      )
        .map((n) => {
          const details = Object.entries(n.contenu)
            .map(([k, v]) => {
              const shown =
                typeof v === 'number'
                  ? k === 'nombre' || k === 'lignes' || k === 'exercice'
                    ? String(v)
                    : fmtFCFA(v)
                  : String(v);
              return `<li>${esc(k)} : ${esc(shown)}</li>`;
            })
            .join('');
          return `<div class="card" style="margin-bottom:10px;"><div class="card-title">Note ${esc(n.numero)} — ${esc(n.titre)}</div><ul style="margin:0;padding-left:18px;font-size:13px;">${details}</ul></div>`;
        })
        .join('');
    }
    void ctx.factures.list().then((factures) => {
      const das2Body = section.querySelector('#conf-das2-table tbody');
      if (!das2Body) return;
      const achats = factures.filter(
        (f) =>
          f.type === 'achat' &&
          (f.date || '').startsWith(annee) &&
          f.statut !== 'annulee' &&
          f.statut !== 'annule',
      );
      das2Body.innerHTML =
        achats.length === 0
          ? '<tr><td colspan="6" style="color:var(--gris);padding:12px;">Aucune facture d’achat sur l’exercice.</td></tr>'
          : achats
              .map((f) => {
                const precompte =
                  f.precompte_montant || Math.round((f.total_ht || 0) * PRECOMPTE_HONORAIRES_RES);
                return `<tr><td>${esc(f.client_id || f.numero)}</td><td>—</td><td class="num">1</td><td class="num">${esc(fmtFCFA(f.total_ht))}</td><td class="num">${esc(fmtFCFA(precompte))}</td><td class="num">${esc(fmtFCFA((f.total_ht || 0) - precompte))}</td></tr>`;
              })
              .join('');
    });
    const cons = section.querySelector('#conf-conservation-stats');
    if (cons) {
      cons.innerHTML = `<table class="data"><tbody>${kvTable([
        ['Écritures de l’exercice', String(rows.length)],
        ['Bulletins de paie', String(bulletins.filter((b) => b.periode.startsWith(annee)).length)],
        ['Conservation jusqu’au', `31/12/${Number(annee) + 10}`],
      ])}</tbody></table>`;
    }
    const systeme =
      section.querySelector<HTMLSelectElement>('#conf-systeme')?.value ?? savedSysteme;
    const sysLabel = section.querySelector<HTMLInputElement>('#conf-systeme-label');
    if (sysLabel) {
      sysLabel.value = systeme === 'smt' ? 'Système Minimal de Trésorerie' : 'Système Normal OHADA';
    }
    const sysDetail = section.querySelector('#conf-systeme-detail');
    if (sysDetail) {
      sysDetail.innerHTML = `<p style="font-size:13px;color:var(--gris);">CA de l’exercice : <strong>${esc(fmtFCFA(produits))}</strong>. Seuil SMT commerce : 60 M FCFA.</p>`;
    }
    const gouvTable = section.querySelector('#conf-gouv-table tbody');
    if (gouvTable) {
      gouvTable.innerHTML = kvTable([
        ['Forme juridique', tenant?.forme_juridique ?? '—'],
        ['Capital social', fmtFCFA(capital)],
        ['NIF', tenant?.nif || '—'],
        ['Effectif', String(effectif)],
      ]);
    }
    const reserveTable = section.querySelector('#conf-reserve-table tbody');
    if (reserveTable) {
      reserveTable.innerHTML = kvTable([
        ['Résultat net', fmtFCFA(reserveCalc.resultat_apres_is)],
        ['Réserve actuelle (111)', fmtFCFA(reserveCalc.reserve_actuelle)],
        ['Dotation de l’exercice', fmtFCFA(reserveCalc.dotation_effective)],
        ['Plafond atteint', reserveCalc.plafond_atteint ? 'Oui' : 'Non'],
      ]);
    }
    const feriesBody = section.querySelector('#conf-feries-table tbody');
    if (feriesBody) {
      feriesBody.innerHTML = CalendrierService.joursFeriesFixes(Number(annee))
        .map((f) => {
          const jour = new Date(f.date).toLocaleDateString('fr-FR', { weekday: 'long' });
          return `<tr><td>${esc(fmtDate(f.date))}</td><td>${esc(jour)}</td><td>${esc(f.nom)}</td></tr>`;
        })
        .join('');
    }
    const seuilsRh = section.querySelector('#conf-seuils-table tbody');
    if (seuilsRh) {
      seuilsRh.innerHTML = kvTable([
        ['SMIG mensuel', fmtFCFA(SMIG)],
        ['Revenu mensuel minimum (RMM)', fmtFCFA(RMM)],
        ['Seuil d’exonération TCS', fmtFCFA(SEUIL_EXONERATION_TCS)],
        ['Délégué du personnel', String(SEUIL_DELEGUE_PERSONNEL)],
        ['CHS', String(SEUIL_CHS)],
        ['Règlement intérieur', String(SEUIL_REGLEMENT_INTERIEUR)],
        ['Effectif actuel', String(effectif)],
      ]);
    }
    const sancBody = section.querySelector('#conf-sanctions-table tbody');
    if (sancBody) {
      sancBody.innerHTML = SanctionsService.NIVEAUX_ORDONNES.map(
        (n, i) => `<tr><td>${i + 1}</td><td>${esc(n)}</td><td>—</td></tr>`,
      ).join('');
    }
    const taxesBody = section.querySelector('#conf-taxes-sect-table tbody');
    if (taxesBody) {
      const secteur = (tenant?.secteur_activite ?? '').toLowerCase();
      const lines: string[] = [];
      if (secteur.includes('banque')) {
        lines.push(
          `<tr><td>TSAB</td><td>Taxe activités bancaires</td><td class="num">1 %</td><td class="num">${esc(fmtFCFA(produits))}</td><td class="num">${esc(fmtFCFA(TaxesSectoriellesService.calculerTSAB(produits)))}</td></tr>`,
        );
      }
      taxesBody.innerHTML =
        lines.join('') ||
        '<tr><td colspan="5" style="color:var(--gris);padding:12px;">Aucune taxe sectorielle pour le profil renseigné dans Mon entreprise.</td></tr>';
    }
    renderStc();
  };
  render();

  const w = window as unknown as Record<string, unknown>;
  w['renderConformite'] = () => render();
  w['renderSTCSalarie'] = () => renderStc();
  w['setProfilFiscal'] = async (v: string) => {
    if (!guardCompta()) return;
    await SecureTenantStore.setJson(tid, 'conformite.profil', v, `nexxus.conformite.${tid}.profil`);
    render();
  };
  w['setPatenteForfait'] = async (v: string) => {
    if (!guardCompta()) return;
    await SecureTenantStore.setJson(
      tid,
      'conformite.patente',
      Number(v) || 0,
      `nexxus.conformite.${tid}.patente`,
    );
    render();
  };
  w['setSystemeComptable'] = async (v: string) => {
    if (!guardCompta()) return;
    await SecureTenantStore.setJson(
      tid,
      'conformite.systeme',
      v,
      `nexxus.conformite.${tid}.systeme`,
    );
    render();
    ToastService.success('Système comptable enregistré');
  };
  w['genererProvisionsExercice'] = async () => {
    if (!guardCompta()) return;
    const annee = anneeCourante();
    const rows = ecritures.filter((e) => yearOf(e) === annee);
    const masseSalariale = bulletins
      .filter((b: Bulletin) => b.periode.startsWith(annee))
      .reduce((s, b) => s + (b.brut_imposable || 0), 0);
    const result = ProvisionsOHADAService.genererEcritures(tid, {
      encours_clients: Math.max(0, soldeCompte(rows, '41')),
      masse_salariale: masseSalariale,
      annee,
    });
    if (result.ecritures.length === 0) {
      ToastService.warning('Aucune dotation à générer (bases nulles)');
      return;
    }
    const ok = await confirmAction({
      title: 'Provisions OHADA',
      message: `Créer ${result.ecritures.length} écriture(s) au 31/12/${annee} pour ${fmtFCFA(result.total_general)} ?`,
      confirmLabel: 'Générer',
      host: section,
    });
    if (!ok) return;
    for (const e of result.ecritures) await ctx.ecritures.add(e);
    await recordAuditEvent(ctx, {
      action: 'create',
      entity: 'ecriture',
      description: `Provisions OHADA ${annee}`,
    });
    ecritures = await ctx.ecritures.list();
    render();
    ToastService.success('Écritures de provisions générées');
  };
  const showLiasse = (html: string): void => {
    const host = section.querySelector('#liasse-result');
    if (host) host.innerHTML = html;
  };
  const regimeCourant = (): RegimeIS => {
    const profil =
      section.querySelector<HTMLSelectElement>('#conf-profil')?.value ?? savedProfil ?? 'normal';
    return PROFIL_TO_REGIME[profil] ?? 'droit_commun';
  };
  w['genererMD2DUI'] = () => {
    const annee = anneeCourante();
    const l = LiasseFiscaleService.genererMD2D(
      ecritures.filter((e) => yearOf(e) === annee),
      annee,
      0,
      0,
      regimeCourant(),
    );
    showLiasse(
      `<div class="card"><div class="card-title">MD2D — ${esc(annee)}</div><table class="data"><tbody>${kvTable(
        [
          ['Chiffre d’affaires', fmtFCFA(l.chiffre_affaires)],
          ['IS dû', fmtFCFA(l.is_du)],
        ],
      )}</tbody></table></div>`,
    );
    ToastService.success(`MD2D ${annee} générée`);
  };
  w['genererISPUI'] = () => {
    const annee = anneeCourante();
    const prev = String(Number(annee) - 1);
    const regime = regimeCourant();
    const md2dPrev = LiasseFiscaleService.genererMD2D(
      ecritures.filter((e) => yearOf(e) === prev),
      prev,
      0,
      0,
      regime,
    );
    const isp = LiasseFiscaleService.genererISP(annee, md2dPrev.is_du);
    showLiasse(
      `<div class="card"><div class="card-title">ISP — ${esc(annee)}</div><table class="data"><tbody>${kvTable(
        [
          ['Total acomptes', fmtFCFA(isp.total_acomptes)],
          ['Solde', fmtFCFA(isp.solde)],
        ],
      )}</tbody></table></div>`,
    );
    ToastService.success(`ISP ${annee} générée`);
  };
  w['genererISAUI'] = () => {
    const annee = anneeCourante();
    const regime = regimeCourant();
    const md2d = LiasseFiscaleService.genererMD2D(
      ecritures.filter((e) => yearOf(e) === annee),
      annee,
      0,
      0,
      regime,
    );
    const prev = LiasseFiscaleService.genererMD2D(
      ecritures.filter((e) => yearOf(e) === String(Number(annee) - 1)),
      String(Number(annee) - 1),
      0,
      0,
      regime,
    );
    const isp = LiasseFiscaleService.genererISP(annee, prev.is_du);
    const isa = LiasseFiscaleService.genererISA(annee, md2d.is_du, isp.total_acomptes);
    showLiasse(
      `<div class="card"><div class="card-title">ISA — ${esc(annee)}</div><table class="data"><tbody>${kvTable(
        [
          ['Solde à payer', fmtFCFA(isa.solde_a_payer)],
          ['Excédent à reporter', fmtFCFA(isa.excedent_a_reporter)],
        ],
      )}</tbody></table></div>`,
    );
    ToastService.success(`ISA ${annee} générée`);
  };
  w['exportDAS2'] = async () => {
    const annee = anneeCourante();
    const factures = await ctx.factures.list();
    const achats = factures.filter((f) => f.type === 'achat' && (f.date || '').startsWith(annee));
    const header = 'Beneficiaire;Nb_factures;Brut_HT;Precompte_IS;Net_verse';
    const lines = achats.map((f) =>
      [
        f.client_id || f.numero,
        '1',
        f.total_ht,
        f.precompte_montant ?? 0,
        (f.total_ht || 0) - (f.precompte_montant || 0),
      ].join(';'),
    );
    downloadCsv([header, ...lines].join('\n'), `das2-${annee}.csv`);
    ToastService.success(`DAS 2 ${annee} exportée`);
  };
  w['inventaireAnnuel'] = async () => {
    if (!guardCompta()) return;
    const annee = anneeCourante();
    const valeur = stocks.reduce((s, a) => s + a.quantite * a.pu, 0);
    const ok = await confirmAction({
      title: 'Inventaire annuel',
      message: `Figer l’inventaire ${annee} (${stocks.length} article(s), ${fmtFCFA(valeur)}) ?`,
      confirmLabel: 'Clôturer',
      host: section,
    });
    if (!ok) return;
    const hist = await loadRegister<{
      annee: string;
      date: string;
      articles: number;
      valeur: number;
    }>(tid, 'inventaires');
    await saveRegister(tid, 'inventaires', [
      { annee, date: new Date().toISOString().slice(0, 10), articles: stocks.length, valeur },
      ...hist.filter((h) => h.annee !== annee),
    ]);
    render();
    ToastService.success(`Inventaire ${annee} clôturé`);
  };
  w['genererReserveLegaleUI'] = async () => {
    if (!guardCompta()) return;
    const annee = anneeCourante();
    const rows = ecritures.filter((e) => yearOf(e) === annee);
    const cr = EtatsFinanciersService.calculerCompteResultat(rows);
    const calc = ReserveLegaleService.calculer(
      annee,
      cr.resultat_net,
      tenant?.capital_social ?? 0,
      Math.max(0, -soldeCompte(rows, '111')),
    );
    if (calc.dotation_effective <= 0) {
      ToastService.warning('Aucune dotation à passer (résultat nul ou plafond atteint)');
      return;
    }
    const ok = await confirmAction({
      title: 'Réserve légale',
      message: `Passer l’écriture ${fmtFCFA(calc.dotation_effective)} au 31/12/${annee} ?`,
      confirmLabel: 'Générer',
      host: section,
    });
    if (!ok) return;
    const date = `31/12/${annee}`;
    const periode = `${annee}-12`;
    await ctx.ecritures.add({
      id: uuid('ecr'),
      tenant_id: tid,
      date,
      periode,
      journal: 'OD',
      compte: '12',
      libelle_compte: 'Résultat net de l exercice',
      libelle: `Dotation réserve légale ${annee}`,
      debit: calc.dotation_effective,
      credit: 0,
      source: 'reserve_legale',
      genere_le: new Date().toISOString(),
    });
    await ctx.ecritures.add({
      id: uuid('ecr'),
      tenant_id: tid,
      date,
      periode,
      journal: 'OD',
      compte: '111',
      libelle_compte: 'Reserve legale',
      libelle: `Dotation réserve légale ${annee}`,
      debit: 0,
      credit: calc.dotation_effective,
      source: 'reserve_legale',
      genere_le: new Date().toISOString(),
    });
    ecritures = await ctx.ecritures.list();
    render();
    ToastService.success('Écriture de réserve légale générée');
  };
  w['simulerIRVM'] = () => {
    const brut = Number(section.querySelector<HTMLInputElement>('#irvm-montant')?.value ?? 0);
    const type = (section.querySelector<HTMLSelectElement>('#irvm-type')?.value ?? 'dividendes') as
      TypeRevenuCapitaux;
    if (brut <= 0) {
      ToastService.warning('Saisissez un montant brut');
      return;
    }
    const r = IRVMService.calculer(brut, type);
    const host = section.querySelector('#irvm-result');
    if (host) {
      host.innerHTML = `<table class="data"><tbody>${kvTable([
        ['Montant brut', fmtFCFA(r.montant_brut)],
        ['Taux', `${(r.taux * 100).toFixed(0)} %`],
        ['IRVM', fmtFCFA(r.irvm)],
        ['Net à verser', fmtFCFA(r.net)],
      ])}</tbody></table>`;
    }
  };
  w['archiverStcDepuisConformite'] = async () => {
    if (!canMutateRh) {
      ToastService.warning('Action réservée aux profils autorisés');
      return;
    }
    const salId = section.querySelector<HTMLSelectElement>('#conf-stc-salarie')?.value ?? '';
    const sal = salaries.find((s: Salarie) => s.id === salId);
    if (!sal) {
      ToastService.warning('Sélectionnez un salarié');
      return;
    }
    const motif = (section.querySelector<HTMLSelectElement>('#conf-stc-motif')?.value ??
      'demission') as MotifSortieStc;
    const result = SoldeToutCompteService.calculer({
      salarie: sal,
      motif,
      config: stcConfig,
      conges,
      ancienneteConfig,
      brut_total_cdd: bulletins
        .filter((b: Bulletin) => b.salarie_id === sal.id)
        .reduce((s, b) => s + (b.brut_imposable || 0), 0),
    });
    const csv = [
      'Libelle;Base;Montant',
      `Salaire global mensuel de reference;Base de calcul;${result.base_mensuelle}`,
      ...result.lignes.slice(1).map((l) => `${l.libelle};${l.base};${l.montant}`),
      `TOTAL;;${result.total}`,
    ].join('\n');
    try {
      const file = new File([csv], `STC-${sal.matricule}.csv`, { type: 'text/csv' });
      await CoffreFortStorageService.addDocument(ctx, tid, file, {
        scope: 'salarie',
        salarie_id: sal.id,
        category: 'Attestations',
      });
      ToastService.success('STC archivé dans le coffre-fort RH');
    } catch (err) {
      downloadBlob(
        new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }),
        `STC-${sal.matricule}.csv`,
      );
      ToastService.warning(
        err instanceof Error ? `${err.message} — fichier téléchargé.` : 'Fichier STC téléchargé.',
      );
    }
  };
};
